import { useEffect, useState } from 'react'
import { Loader2, AlertCircle, KeyRound, Plus } from 'lucide-react'
import { Modal, ModalHeader } from '@/components/Modal'
import { PLATFORM_LIST, type PlatformId } from '@/lib/platforms'
import { useStore } from '@/lib/store'
import {
  fetchProviders,
  connectBluesky,
  connectOAuth,
  type ConnectResult,
  type ProviderInfo,
} from '@/lib/connect'
import { getBillingSummary, reloadWorkspaceData } from '@/lib/data'
import { reconcileConnectedChannel } from '@/lib/reconcile-connection'
import { trackEvent } from '@/lib/posthog'
import { DisconnectChannelButton } from './DisconnectChannelButton'
import { ChannelAvatar } from '@/components/ChannelAvatar'
import { PlatformLogo } from '@/components/PlatformLogo'
import clsx from 'clsx'

const CONNECTABLE: PlatformId[] = ['bluesky', 'mastodon', 'linkedin', 'x', 'threads', 'facebook', 'instagram']
const CONNECTABLE_PLATFORMS = PLATFORM_LIST.filter((p) => CONNECTABLE.includes(p.id))
const EMPTY_BSKY = { identifier: '', appPassword: '', service: '' }

const defaultConnectApi = { fetchProviders, getBillingSummary }

const knownChannelIds = () => new Set(useStore.getState().channels.map((c) => c.id))
const expiredChannelIds = () =>
  new Set(useStore.getState().channels.filter((c) => c.status === 'needs_reconnect').map((c) => c.id))

export function ConnectChannelsModal({
  open,
  onClose,
  api = defaultConnectApi,
}: {
  open: boolean
  onClose: () => void
  api?: typeof defaultConnectApi
}) {
  const channels = useStore((s) => s.channels)
  const [connecting, setConnecting] = useState<PlatformId | null>(null)
  const [providers, setProviders] = useState<Record<string, ProviderInfo>>({})
  const [providersReady, setProvidersReady] = useState(false)
  const [providersError, setProvidersError] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [form, setForm] = useState<PlatformId | null>(null) // which inline form is open
  const [bsky, setBsky] = useState(EMPTY_BSKY)
  const [mastoHost, setMastoHost] = useState('')
  const [accountCap, setAccountCap] = useState<number | null>(null)

  useEffect(() => {
    if (!open) return
    setProvidersReady(false)
    setProvidersError(null)
    api.fetchProviders().then((list) => {
      const map: Record<string, ProviderInfo> = {}
      for (const p of list) map[p.provider] = p
      setProviders(map)
      if (list.length === 0) {
        setProvidersError('The connection service is not reachable right now.')
      }
      setProvidersReady(true)
    })
  }, [open, api])

  // Mirror the plan's account cap so a full plan is not sent through OAuth only
  // to be refused. The server check in upsertConnectedAccount stays the real gate,
  // and the cap only applies while limits are enforced for this workspace.
  useEffect(() => {
    if (!open) return
    let live = true
    api
      .getBillingSummary()
      .then((summary) => {
        if (live) setAccountCap(summary.access?.limitsEnforced ? summary.usage.accounts.limit : null)
      })
      .catch(() => {
        if (live) setAccountCap(null)
      })
    return () => {
      live = false
    }
  }, [open, api])
  const atCap = accountCap != null && channels.length >= accountCap

  const finishLive = async (
    platform: PlatformId,
    result: ConnectResult,
    knownIds: Set<string>,
    expiredIds: Set<string>,
  ) => {
    setForm(null)
    // Signing in again as an account whose sign-in expired is a reconnect, not
    // a duplicate.
    const reconnected = result.ok && result.added === false && !!result.accountId && expiredIds.has(result.accountId)
    const shouldReconcile = result.ok || result.error === 'cancelled'
    const connectedAfterRefresh = shouldReconcile
      ? await reconcileConnectedChannel({
          platform,
          accountId: result.accountId,
          knownIds,
          reload: reloadWorkspaceData,
          readChannels: () => useStore.getState().channels,
        })
      : false
    setConnecting(null)
    if (reconnected) {
      setError(null)
      trackEvent('account_reconnected', { platform })
    } else if (result.ok && result.added === false) {
      trackEvent('account_connect_failed', { platform, error: 'already_connected' })
      setError(prettyError('already_connected', platform))
    } else if (connectedAfterRefresh) {
      setError(null)
      if (platform === 'bluesky') setBsky(EMPTY_BSKY)
      trackEvent('account_connect_succeeded', { platform, method: 'live' })
    } else if (result.ok) {
      trackEvent('account_connect_failed', { platform, error: 'connection_not_visible' })
      setError(prettyError('connection_not_visible', platform))
    } else if (result.error && result.error !== 'cancelled') {
      trackEvent('account_connect_failed', { platform, error: result.error })
      setError(prettyError(result.error, platform))
    }
  }

  const connect = async (platform: PlatformId) => {
    setError(null)
    const info = providers[platform]
    if (!info) {
      setError('The connection service is not reachable right now.')
      return
    }
    if (!info.configured) {
      setError(prettyError('not_configured', platform))
      return
    }
    // Providers needing a small input first.
    if (platform === 'bluesky') return setForm('bluesky')
    if (platform === 'mastodon') return setForm('mastodon')
    // OAuth popup.
    setConnecting(platform)
    const knownIds = knownChannelIds()
    const expiredIds = expiredChannelIds()
    finishLive(platform, await connectOAuth(platform), knownIds, expiredIds)
  }

  const submitBluesky = async () => {
    if (!bsky.identifier || !bsky.appPassword) return
    setConnecting('bluesky')
    const knownIds = knownChannelIds()
    const expiredIds = expiredChannelIds()
    finishLive(
      'bluesky',
      await connectBluesky({
        identifier: bsky.identifier.trim(),
        appPassword: bsky.appPassword.trim(),
        service: bsky.service.trim() || undefined,
      }),
      knownIds,
      expiredIds,
    )
  }

  const submitMastodon = async () => {
    if (!mastoHost.trim()) return
    setConnecting('mastodon')
    const knownIds = knownChannelIds()
    const expiredIds = expiredChannelIds()
    finishLive('mastodon', await connectOAuth('mastodon', { host: mastoHost.trim() }), knownIds, expiredIds)
  }

  return (
    <Modal open={open} onClose={onClose} size="md" ariaLabel="Connect channels">
      <ModalHeader title="Connect channels" onClose={onClose} />
      <div className="flex-1 overflow-y-auto">
        {error && (
          <div className="m-4 mb-0 flex items-start gap-2 rounded-xl border border-accent-red/40 bg-accent-red/10 p-3 text-sm text-accent-red">
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}
        {atCap && (
          <div className="m-4 mb-0 flex items-start gap-2 rounded-xl border border-line bg-panel p-3 text-sm text-ink-700">
            <AlertCircle size={16} className="mt-0.5 shrink-0 text-ink-400" />
            <span>Your plan includes {accountCap} accounts. Upgrade in Settings to add more.</span>
          </div>
        )}
        {providersError && (
          <div className="m-4 mb-0 flex items-start gap-2 rounded-xl border border-accent-amber/40 bg-accent-amber/10 p-3 text-sm text-ink-700">
            <AlertCircle size={16} className="mt-0.5 shrink-0 text-accent-amber" />
            <span>{providersError}</span>
          </div>
        )}
        <div className="divide-y divide-line">
          {CONNECTABLE_PLATFORMS.map((p) => {
            const accounts = channels.filter((c) => c.platform === p.id)
            const isBusy = connecting === p.id
            const info = providers[p.id]
            const needsSetup = providersReady && info && !info.configured
            const unavailable = providersReady && (!info || needsSetup)
            const blocked = unavailable || !providersReady || atCap
            return (
              <div key={p.id}>
                <div className="grid min-h-16 grid-cols-[minmax(0,1fr)_auto] items-center gap-3 px-5 py-2.5">
                  <div className="flex min-w-0 items-center gap-3">
                    <PlatformLogo platform={p.id} size={32} />
                    <span className="truncate font-display text-sm font-semibold text-ink-900">{p.name}</span>
                  </div>

                  <button
                    type="button"
                    disabled={isBusy || blocked}
                    onClick={() => void connect(p.id)}
                    className={clsx(
                      'btn min-w-24 justify-center rounded-lg px-3 py-2 text-xs',
                      blocked
                        ? 'cursor-not-allowed bg-panel text-ink-400 opacity-70'
                        : accounts.length > 0
                          ? 'bg-surface text-ink-900 shadow-hairline hover:bg-hover'
                          : 'bg-brand-500 text-white hover:bg-brand-600',
                    )}
                  >
                    {isBusy ? (
                      <>
                        <Loader2 size={14} className="animate-spin" /> Connecting...
                      </>
                    ) : !providersReady ? (
                      'Checking...'
                    ) : unavailable ? (
                      'Unavailable'
                    ) : accounts.length > 0 ? (
                      <>
                        <Plus size={14} /> Add account
                      </>
                    ) : (
                      'Connect now'
                    )}
                  </button>
                </div>

                {accounts.map((channel) => (
                  <div
                    key={channel.id}
                    className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 pb-3 pl-16 pr-5"
                  >
                    <div className="flex min-w-0 items-center gap-2.5">
                      <ChannelAvatar channel={channel} size="sm" showBadge={false} />
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium text-ink-900">{channel.name}</span>
                        {channel.handle && (
                          <span className="block max-w-full truncate text-[11px] leading-4 text-ink-400">
                            {channel.handle}
                          </span>
                        )}
                      </span>
                    </div>
                    <div className="flex items-center justify-end gap-3">
                      {channel.status === 'needs_reconnect' ? (
                        <button
                          type="button"
                          disabled={isBusy || unavailable || !providersReady}
                          onClick={() => void connect(p.id)}
                          className="btn rounded-lg bg-accent-amber/15 px-3 py-1.5 text-xs text-ink-900 hover:bg-accent-amber/25"
                        >
                          Sign-in expired. Reconnect
                        </button>
                      ) : (
                        <p className="flex items-center gap-1.5 text-xs font-medium text-accent-green">
                          <span className="h-1.5 w-1.5 rounded-full bg-accent-green" />
                          Connected
                        </p>
                      )}
                      <DisconnectChannelButton channel={channel} compact />
                    </div>
                  </div>
                ))}

                {form === p.id && p.id === 'bluesky' && (
                  <div className="border-t border-line bg-panel px-5 py-4">
                    <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-ink-900">
                      <KeyRound size={15} className="text-brand-500" /> Connect Bluesky
                    </div>
                    <p className="mb-3 text-xs text-ink-500">
                      Use an <strong>app password</strong> from Bluesky Settings, App Passwords, not
                      your main password.
                    </p>
                    <div className="grid gap-2">
                      <input
                        className="input"
                        placeholder="handle.bsky.social"
                        value={bsky.identifier}
                        onChange={(e) => setBsky({ ...bsky, identifier: e.target.value })}
                      />
                      <input
                        className="input"
                        type="password"
                        placeholder="app password (xxxx-xxxx-xxxx-xxxx)"
                        value={bsky.appPassword}
                        onChange={(e) => setBsky({ ...bsky, appPassword: e.target.value })}
                      />
                      <div className="flex justify-end gap-2">
                        <button className="btn-ghost" onClick={() => setForm(null)}>
                          Cancel
                        </button>
                        <button
                          className="btn-primary"
                          onClick={submitBluesky}
                          disabled={connecting === 'bluesky'}
                        >
                          {connecting === 'bluesky' ? 'Connecting...' : 'Connect'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {form === p.id && p.id === 'mastodon' && (
                  <div className="border-t border-line bg-panel px-5 py-4">
                    <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-ink-900">
                      <KeyRound size={15} className="text-brand-500" /> Connect Mastodon
                    </div>
                    <p className="mb-3 text-xs text-ink-500">
                      Enter your instance domain. We register an app there and open its login.
                    </p>
                    <div className="grid gap-2">
                      <input
                        className="input"
                        placeholder="mastodon.social"
                        value={mastoHost}
                        onChange={(e) => setMastoHost(e.target.value)}
                      />
                      <div className="flex justify-end gap-2">
                        <button className="btn-ghost" onClick={() => setForm(null)}>
                          Cancel
                        </button>
                        <button
                          className="btn-primary"
                          onClick={submitMastodon}
                          disabled={connecting === 'mastodon'}
                        >
                          {connecting === 'mastodon' ? 'Opening...' : 'Continue'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </Modal>
  )
}

function prettyError(code: string, platform: PlatformId): string {
  const name = PLATFORM_LIST.find((p) => p.id === platform)?.name ?? 'that network'
  switch (code) {
    case 'already_connected':
      return platform === 'bluesky'
        ? 'That account is already connected.'
        : `That account is already connected. Switch accounts on ${name}, then add again.`
    case 'limit_exceeded':
      return 'Your plan’s account limit is reached. Upgrade in Settings to add more.'
    case 'not_configured':
      return 'This network needs our developer app credentials, which aren’t set up yet.'
    case 'popup_blocked':
      return 'Your browser blocked the connect popup. Allow popups and try again.'
    case 'host_required':
      return 'Enter your Mastodon instance domain first.'
    case 'unauthorized':
      return 'Your session expired. Sign in again.'
    case 'connection_not_visible':
      return 'The network approved the connection, but it has not appeared here yet. Try connecting again.'
    default:
      return `Couldn’t connect: ${code}`
  }
}
